import { AppProblemModel } from '~/services/api/AppProblemModel'
import type { StoreAvailabilityCheckSuccess } from '~/services/api/generated'
import type { PublicStore } from '~/types/PublicStore'
import type { AvailabilityCheckAttempt } from './AvailabilityCheckAttempt'
import type { StoreAvailabilityCheckOutcome } from './StoreAvailabilityCheckOutcome'
import { failedAvailabilityOutcome } from './failedAvailabilityOutcome'
import { isStoreClosureProblem } from './isStoreClosureProblem'
import { resolveAvailabilityProblemMessage } from './resolveAvailabilityProblemMessage'
import type { StoreOrderingContextInputs } from './StoreOrderingContextInputs'
import type { TranslateFn } from './TranslateFn'

function deriveAvailabilityOutcome(
  parsed: StoreAvailabilityCheckSuccess,
  reasonText: string,
  t: TranslateFn
): StoreAvailabilityCheckOutcome {
  return parsed.data.available && parsed.success
    ? { result: parsed, status: 'available' }
    : {
        get message() {
          return reasonText || t('commerce.availabilityErrorModal.notAvailableForRequest')
        },
        result: parsed,
        status: 'unavailable'
      }
}

function resolveLoadedStoreSlug(
  store: {
    readonly value: PublicStore | null
  },
  expectedSlug: { readonly value: string }
): string | null {
  const slug = store.value?.slug
  return slug == null || slug === '' || slug !== expectedSlug.value ? null : slug
}

// Split out of `performAvailabilityCheckAttempt` so that function stays a flat sequence of
// staleness guards instead of nesting this apply/derive sequence deeper.
function applyAvailabilityResponse(
  response: Exclude<StoreAvailabilityCheckSuccess, string>,
  requestKey: string,
  deps: Pick<
    AttemptAvailabilityCheckDeps,
    'applyAvailabilityResult' | 'getFirstAvailabilityReason' | 't'
  >
): StoreAvailabilityCheckOutcome {
  deps.applyAvailabilityResult(response, requestKey)
  return deriveAvailabilityOutcome(response, deps.getFirstAvailabilityReason(), deps.t)
}

interface AttemptAvailabilityCheckDeps {
  store: { readonly value: PublicStore | null }
  expectedSlug: { readonly value: string }
  // Already resolved in the store's own timezone (see resolveSelectedRequestDateForApi),
  // handling an unparseable persisted date the same way a missing one is handled; passed
  // through instead of letting fetchAvailability re-derive its own, independently, from
  // useSearchStore's raw eventDateTime.
  eventDate: { readonly value: string }
  eventTime: { readonly value: string | null | undefined }
  availabilityContextKey: { readonly value: string }
  // Marks one availability-check attempt in flight/settled against the context's shared
  // in-flight counter, independent of whether this attempt's key is still current.
  checking: { start: () => void; stop: () => void }
  fetchAvailability: StoreOrderingContextInputs['fetchAvailability']
  applyAvailabilityResult: (data: StoreAvailabilityCheckSuccess, key: string) => void
  // useStoreClosure folds this into the canonical closure projection, which the full-page
  // block displays; this never also opens the availability modal.
  applyClosureFailure: (message: () => string, key: string) => void
  // Opens the availability modal for any other availability-check failure.
  applyGenericFailure: (message: () => string, key: string) => void
  getFirstAvailabilityReason: () => string
  timezone: { readonly value: string }
  t: TranslateFn
  onTemporaryClosureDetected?: (() => void) | undefined
  onAvailabilityFetchError?: ((error: unknown) => void) | undefined
}

// Not pure, unlike `deriveAvailabilityOutcome`: dispatching the closure-vs-generic-error
// callback is a real side effect; everything else derives from `err`.
function resolveAvailabilityCheckFailure(
  err: unknown,
  timezone: string,
  t: TranslateFn,
  onTemporaryClosureDetected: (() => void) | undefined,
  onAvailabilityFetchError: ((err: unknown) => void) | undefined
): { message: () => string; isClosure: boolean } {
  const problem = AppProblemModel.normalize(err)
  const isClosure = isStoreClosureProblem(problem)
  if (isClosure) {
    onTemporaryClosureDetected?.()
  } else {
    onAvailabilityFetchError?.(err)
  }
  const message = () =>
    resolveAvailabilityProblemMessage(
      problem,
      timezone,
      t,
      t('commerce.availabilityErrorModal.verifyFailed')
    )
  return { isClosure, message }
}

// Split out of `performAvailabilityCheckAttempt` for the same reason as
// `applyAvailabilityResponse` above.
function buildAvailabilityCheckFailureOutcome(
  err: unknown,
  requestKey: string,
  deps: Pick<
    AttemptAvailabilityCheckDeps,
    | 'timezone'
    | 't'
    | 'onTemporaryClosureDetected'
    | 'onAvailabilityFetchError'
    | 'applyClosureFailure'
    | 'applyGenericFailure'
  >
): StoreAvailabilityCheckOutcome {
  const { isClosure, message } = resolveAvailabilityCheckFailure(
    err,
    deps.timezone.value,
    deps.t,
    deps.onTemporaryClosureDetected,
    deps.onAvailabilityFetchError
  )

  if (isClosure) {
    deps.applyClosureFailure(message, requestKey)
    return {
      get message() {
        return message()
      },
      status: 'closed'
    }
  }

  deps.applyGenericFailure(message, requestKey)
  return failedAvailabilityOutcome(message)
}

// Resolves `{ settled: false, reason: 'superseded' }` if a newer selection invalidates the
// response before or after the fetch; split out so the caller stays a plain guard + try/finally.
async function settleAvailabilityAttempt(
  slug: string,
  options: { checkMenu?: boolean },
  requestKey: string,
  deps: AttemptAvailabilityCheckDeps
): Promise<AvailabilityCheckAttempt> {
  try {
    const eventTime = deps.eventTime.value
    const response = await deps.fetchAvailability(slug, {
      checkMenu: options.checkMenu === true,
      eventDate: deps.eventDate.value,
      timezone: deps.timezone.value,
      ...(eventTime != null ? { eventTime } : {})
    })
    if (deps.availabilityContextKey.value !== requestKey) {
      return { reason: 'superseded', settled: false }
    }
    return {
      outcome: applyAvailabilityResponse(response, requestKey, deps),
      settled: true
    }
  } catch (err) {
    if (deps.availabilityContextKey.value !== requestKey) {
      return { reason: 'superseded', settled: false }
    }
    return { outcome: buildAvailabilityCheckFailureOutcome(err, requestKey, deps), settled: true }
  }
}

/**
 * Runs one attempt of an availability check. A superseded context resolves as
 * `{ settled: false, reason: 'superseded' }`; no store loaded resolves as `reason: 'store-not-loaded'`.
 */
export async function performAvailabilityCheckAttempt(
  options: { checkMenu?: boolean },
  deps: AttemptAvailabilityCheckDeps
): Promise<AvailabilityCheckAttempt> {
  const slug = resolveLoadedStoreSlug(deps.store, deps.expectedSlug)
  if (slug === null) return { reason: 'store-not-loaded', settled: false }

  const requestKey = deps.availabilityContextKey.value
  deps.checking.start()
  try {
    return await settleAvailabilityAttempt(slug, options, requestKey, deps)
  } finally {
    deps.checking.stop()
  }
}
